/**
 * Générateur — Il utilise l'inverse pour calculer.
 * Attendu ID : 148 | Niveau : 4ème | Domaine : Nombres (desc 11, O13)
 *
 * Source doc prof (NOMBRE ET CALCUL, cluster 3) : Ex.3 « 1/(1/15) ; 1/(7/4)… »,
 * Ex.4 « entoure les nombres dont 10/7 est l'inverse » (= tous égaux à 7/10),
 * Ex.5 tableau x/inverse/opposé (0 sans inverse), Ex.6 langue emboîtée
 * « l'inverse du quart de l'opposé de 5 » (= −4/5). Plan validé 2026-07-14 :
 *
 *   Carte « inverse »     — inverse d'un entier, d'un décimal ou d'une fraction (2 trous num/den)
 *   Carte « reconnaitre » — nombres dont l'inverse est X (QCM multiple)
 *   Carte « emboite »     — composition verbale « l'inverse du quart de… » (saisie)
 *   Carte « vf »          — inverse vs opposé, « 0 n'a pas d'inverse » (Vrai/Faux)
 *
 * Revue 2026-09-22 (mesures sur dix mille tirages par carte) : aucune carte
 * supprimée.
 *   - « emboite » : 68,8 % des tirages ne contenaient aucune inversion (c'était
 *     de la fraction de fraction, attendu 147). Chaque séquence contient au
 *     moins une inversion ; deux opérations réciproques consécutives (inverse de
 *     l'inverse, opposé de l'opposé, double de la moitié, triple du tiers) sont
 *     interdites ; un résultat entier (28,3 % des tirages) ou égal au départ
 *     est rejeté ; un départ valant ±1 après réduction est exclu. Consigne
 *     « fraction irréductible, dénominateur positif » (six quarts pour le
 *     double de trois quarts était compté faux).
 *   - « inverse » : entiers élargis à 20, 50 et 100 ; inverse d'un décimal
 *     ajouté (liste fermée 0,4 ; 0,6 ; 0,75 ; 0,8 ; 1,2 ; 1,25 ; 1,5 ; 2,5 ; 3,5,
 *     dont l'inverse n'est jamais entier) ; consigne « fraction irréductible ».
 *   - « reconnaitre » : le nombre est signé (avant, toujours positif : le
 *     distracteur « opposé de l'inverse » ne jouait pas) ; distracteurs
 *     distincts en valeur.
 *   - « vf » : quatre nombres distincts (deux lignes identiques sortaient dans
 *     1,6 % des tirages), au moins une ligne vraie et une fausse.
 *   - Parenthèses explicatives des consignes réécrites en phrases.
 *
 * Arithmétique EXACTE (toolkit rationnel). Les entrées à inverser ont un
 * numérateur ≠ ±1, et les décimaux un inverse non entier → l'inverse a toujours
 * un dénominateur > 1 (2 trous valides).
 *
 * Revue 2026-09-23 (typographie, règle prof) : les fractions affichées passent
 * en KaTeX, empilées (helpers tex/fracTrouTex de generators/utils.js) : énoncés,
 * options du QCM « reconnaitre », lignes du tableau V/F. Les phrases à trous
 * deviennent des lignes KaTeX globales (entières entre $…$), le français en
 * \text{}, le nombre en maths, la fraction attendue avec ses deux trous en
 * triple accolade : « inverse » sur une ligne, « emboite » en deux lignes (la
 * composition verbale, puis « = » et la fraction) parce que réunies, les plus
 * longues dépasseraient soixante caractères et se feraient couper à
 * l'impression. Un décimal s'écrit 1{,}25 (virgule collée en mode maths) ; un
 * fragment qui commence par un moins est groupé entre accolades, sinon, après
 * le \mathrm{} posé en tête par tex() ou après un \text{}, le moins devient
 * un opérateur binaire espacé. Les paramètres du harnais (_meta.params)
 * gardent l'écriture ASCII.
 *
 * Règle prof 2026-09-23 (composition des tableaux vrai-faux) : quatre lignes
 * au maximum, sinon c'est trop long à lire. « vf » passe de cinq lignes à
 * quatre : trois lignes tirées sur des nombres distincts (au moins une
 * « inverse » et une « opposé », la troisième de type tiré, au moins une
 * vraie et une fausse parmi les trois) et la ligne sur le 0, gardée parce que
 * « 0 n'a pas d'inverse » est un point explicite du doc prof (Ex.5).
 */

import { randomChoice, randInt, shuffle, makeTexteTrou, makeQcm, makeVraiFaux, tex, fracTrouTex } from '../../../utils';
import { rat, ratMul, ratEq, ratFromStr, fmtFrac, decExact, pgcd } from './nombres';

const ATTENDU_ID = 148;

const L_INVERSE = 'Calculer l\'inverse d\'un nombre';
const L_RECONNAITRE = 'Reconnaître les nombres dont l\'inverse est donné';
const L_EMBOITE = 'Calculer une expression emboîtée (inverse, opposé, fractions)';
const L_VF = 'Distinguer inverse et opposé';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Affichage (revue 2026-09-23) : fragment LaTeX d'un rationnel, fraction
// empilée, signe devant la barre, entier nu si q = 1.
const frag = (r) => {
    const s = r.p < 0 ? '-' : '';
    const a = Math.abs(r.p);
    return r.q === 1 ? `${s}${a}` : `${s}\\dfrac{${a}}{${r.q}}`;
};
// Un fragment qui commence par un moins est groupé (moins collé, cf. en-tête).
const grp = (f) => (f.startsWith('-') ? `{${f}}` : f);
const K = (f) => tex(grp(f));                          // un nombre seul, en $…$
const decTex = (brut) => brut.replace(',', '{,}');     // « -1,25 » → « -1{,}25 »
const T = (s) => `\\text{${s}}`;                       // du français dans une ligne KaTeX
const inv = (r) => rat(r.q, r.p);
const opp = (r) => ({ p: -r.p, q: r.q });

// Fraction a/b réduite avec |a| ≠ 1 et b > 1 : son inverse a un dénominateur > 1.
const fractionInversable = () => {
    for (let i = 0; i < 80; i++) {
        const q = randInt(2, 9);
        const p = randInt(2, 3 * q) * randomChoice([1, -1]);
        const r = rat(p, q);
        if (Math.abs(r.p) !== 1 && r.q !== 1) return r;
    }
    return rat(7, 4);
};
// Nombre à inverser (carte « vf ») : entier ±(2..12) OU fraction inversable.
const inversable = () => (randomChoice([true, false])
    ? rat(randomChoice([1, -1]) * randInt(2, 12), 1)
    : fractionInversable());

// Carte « inverse » (revue 2026-09-22) : entier ±(2..12, 20, 50, 100), décimal
// d'une liste fermée dont l'inverse n'est pas entier, ou fraction inversable.
// Rend { r, brut, latex } : la valeur exacte, son écriture ASCII (paramètres)
// et son fragment LaTeX (affichage).
const ENTIERS_A_INVERSER = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 20, 50, 100];
const DECIMAUX_A_INVERSER = ['0,4', '0,6', '0,75', '0,8', '1,2', '1,25', '1,5', '2,5', '3,5'];
const aInverser = () => {
    const s = randomChoice([1, -1]);
    const type = randInt(1, 3);
    if (type === 1) {
        const n = s * randomChoice(ENTIERS_A_INVERSER);
        return { r: rat(n, 1), brut: String(n), latex: String(n) };
    }
    if (type === 2) {
        const brut = `${s < 0 ? '-' : ''}${randomChoice(DECIMAUX_A_INVERSER)}`;
        return { r: ratFromStr(brut), brut, latex: decTex(brut) };
    }
    const r = fractionInversable();
    return { r, brut: fmtFrac(r), latex: frag(r) };
};

// ═════════ Carte « inverse » — 2 trous num/den ═════════

const genInverse = () => {
    const { r: X, brut, latex } = aInverser();
    const I = inv(X);
    // Ligne KaTeX globale : le français en \text{}, le nombre en maths, la
    // fraction attendue empilée avec ses deux trous (triple accolade).
    const phrase = `$${T('L\'inverse de ')}${grp(latex)}${T(' est ')}${fracTrouTex(`{{${I.p}}}`, `{{${I.q}}}`)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Donne l'inverse de <b>${K(latex)}</b> en <b>fraction irréductible</b>, avec un <b>dénominateur positif</b>.</p>`
        + '<p>Écris le numérateur, puis le dénominateur.</p>',
        { ...opts(L_INVERSE), mode: 'saisie' });
    payload._meta.params = { X: brut, inverse: fmtFrac(I) };
    return payload;
};

// ═════════ Carte « reconnaitre » — QCM multiple ═════════

const genReconnaitre = () => {
    // X = ±a/b (a, b ≥ 2 premiers entre eux, a ≠ b) ; les bonnes réponses sont
    // les écritures de 1/X = ±b/a. Signe tiré (revue 2026-09-22).
    let a = 3; let b = 4;                                  // repli déterministe
    for (let i = 0; i < 60; i++) {
        const a0 = randInt(2, 9); const b0 = randInt(2, 9);
        if (a0 !== b0 && pgcd(a0, b0) === 1) { a = a0; b = b0; break; }
    }
    const X = rat(randomChoice([1, -1]) * a, b);
    const cible = inv(X);                                  // ±b/a
    // Bonnes réponses : l'inverse réduit, une écriture non réduite, l'écriture
    // décimale si elle est finie (une fois sur deux). Chaque option porte son
    // écriture ASCII (paramètres) et son affichage KaTeX.
    const k = randomChoice([2, 3]);
    const nonReduit = { p: cible.p * k, q: cible.q * k };
    const bonnes = [
        { ascii: fmtFrac(cible), affiche: K(frag(cible)) },
        { ascii: fmtFrac(nonReduit), affiche: K(frag(nonReduit)) },
    ];
    const d = decExact(cible);
    if (d !== null && randomChoice([true, false])) bonnes.push({ ascii: d, affiche: K(decTex(d)) });

    // Distracteurs : jamais égaux à la cible ni entre eux, en VALEUR.
    const distract = [];
    const add = (r) => {
        if (r.p !== 0 && !ratEq(r, cible) && !distract.some(x => ratEq(x, r))) distract.push(r);
    };
    add(X);                                                // X lui-même (piège inverse/valeur)
    add(opp(cible));                                       // l'opposé de l'inverse
    add(rat(cible.q, cible.p + 1));                        // dénominateur décalé de 1

    const choix = shuffle([...bonnes.map(b => b.affiche), ...distract.map(r => K(frag(r)))]);
    const affichesBons = new Set(bonnes.map(b => b.affiche));
    const correct = choix.filter(c => affichesBons.has(c));
    const payload = makeQcm(choix, correct,
        `<p>Coche <b>tous</b> les nombres dont l'inverse est <b>${K(frag(X))}</b>.</p>`,
        opts(L_RECONNAITRE), true);
    payload._meta.params = { X: fmtFrac(X), cible: fmtFrac(cible), correct: bonnes.map(b => b.ascii) };
    return payload;
};

// ═════════ Carte « emboite » — composition verbale (saisie) ═════════

const OPS = [
    { kw: 'inverse', art: 'l\'inverse', con: 'de l\'inverse', apply: inv },
    { kw: 'opposé', art: 'l\'opposé', con: 'de l\'opposé', apply: opp },
    { kw: 'quart', art: 'le quart', con: 'du quart', apply: r => ratMul(r, rat(1, 4)) },
    { kw: 'tiers', art: 'le tiers', con: 'du tiers', apply: r => ratMul(r, rat(1, 3)) },
    { kw: 'moitié', art: 'la moitié', con: 'de la moitié', apply: r => ratMul(r, rat(1, 2)) },
    { kw: 'double', art: 'le double', con: 'du double', apply: r => ratMul(r, rat(2, 1)) },
    { kw: 'triple', art: 'le triple', con: 'du triple', apply: r => ratMul(r, rat(3, 1)) },
];
const OP_INVERSE = OPS[0];
// Deux opérations réciproques qui se suivent s'annulent : interdites (revue 2026-09-22).
const RECIPROQUES = new Set(['inverse|inverse', 'opposé|opposé', 'double|moitié', 'moitié|double', 'triple|tiers', 'tiers|triple']);
const seqValide = (seq) => seq.every((o, i) => i === 0 || !RECIPROQUES.has(`${seq[i - 1].kw}|${o.kw}`));
// Départ : entier ±(2..12) ou fraction simple (réduite par rat ; ±1 rejeté par l'appelant).
const departAlea = () => (randomChoice([true, false])
    ? rat(randomChoice([1, -1]) * randInt(2, 12), 1)
    : rat(randomChoice([1, -1]) * randInt(2, 15), randInt(2, 6)));
const appliquer = (seq, start) => {
    let v = start;
    for (let i = seq.length - 1; i >= 0; i--) v = seq[i].apply(v);
    return v;
};

const genEmboite = () => {
    let seq = [OP_INVERSE, OPS[2]];                        // repli déterministe : l'inverse du quart de 3 = 4/3
    let start = rat(3, 1);
    for (let essai = 0; essai < 200; essai++) {
        const n = randInt(2, 3);
        const s = Array.from({ length: n }, () => randomChoice(OPS));
        s[randInt(0, n - 1)] = OP_INVERSE;                 // au moins une inversion
        if (!seqValide(s)) continue;
        const d = departAlea();
        if (Math.abs(d.p) === 1 && d.q === 1) continue;    // ±1 : l'inverse ne change rien
        const w = appliquer(s, d);
        if (w.q === 1 || ratEq(w, d)) continue;            // résultat entier, ou retour au départ
        seq = s; start = d;
        break;
    }
    const v = appliquer(seq, start);
    const mots = seq[0].art + seq.slice(1).map(o => ` ${o.con}`).join('') + ' de';   // « l'inverse du quart de »
    // Phrase à trous en deux lignes KaTeX globales (modèle gen_149) : la
    // composition verbale en \text{} avec son nombre de départ en maths, puis
    // « = » et la fraction attendue avec ses deux trous.
    const payload = makeTexteTrou(`$${T(`${mots} `)}${grp(frag(start))}$\n$= ${fracTrouTex(`{{${v.p}}}`, `{{${v.q}}}`)}$`,
        '<p>Calcule cette expression et donne le résultat en <b>fraction irréductible</b>, avec un <b>dénominateur positif</b>.</p>'
        + `<p><b>${mots} ${K(frag(start))}</b></p>`
        + '<p>Écris le numérateur, puis le dénominateur.</p>',
        { ...opts(L_EMBOITE), mode: 'saisie' });
    payload._meta.params = { phrase: `${mots} ${fmtFrac(start)}`, res: fmtFrac(v) };
    return payload;
};

// ═════════ Carte « vf » — inverse vs opposé (tableau) ═════════

// Valeurs fixes de repli (six pour trois tirages : il en reste toujours une libre).
const REPLI_VF = [rat(2, 1), rat(-3, 1), rat(7, 4), rat(-5, 2), rat(9, 1), rat(5, 3)];

const genVf = () => {
    const lignes = [];
    const bonnes = [];
    const pousser = (txt, vrai) => { lignes.push(txt); bonnes.push(vrai ? 0 : 1); };

    // Trois nombres distincts (revue 2026-09-22 : deux lignes identiques sortaient
    // dans 1,6 % des tirages) ; boucle bornée, repli déterministe.
    const vus = new Set();
    const tirer = () => {
        for (let i = 0; i < 60; i++) {
            const X = inversable();
            if (!vus.has(fmtFrac(X))) { vus.add(fmtFrac(X)); return X; }
        }
        const X = REPLI_VF.find(r => !vus.has(fmtFrac(r)));
        vus.add(fmtFrac(X));
        return X;
    };
    // Quatre lignes (règle prof 2026-09-23) : trois lignes tirées, au moins une
    // « inverse » et une « opposé » (la troisième de type tiré), au moins une
    // vraie et une fausse par construction, + 1 ligne sur le 0.
    const types = shuffle(['inverse', 'oppose', randomChoice(['inverse', 'oppose'])]);
    const bons = shuffle([true, false, randomChoice([true, false])]);
    types.forEach((type, t) => {
        const X = tirer();
        if (type === 'inverse') {
            const R = bons[t] ? inv(X) : opp(X);              // faux fréquent : donner l'opposé
            pousser(`L'inverse de ${K(frag(X))} est ${K(frag(R))}.`, ratEq(R, inv(X)));
        } else {
            const R = bons[t] ? opp(X) : inv(X);
            pousser(`L'opposé de ${K(frag(X))} est ${K(frag(R))}.`, ratEq(R, opp(X)));
        }
    });
    if (randomChoice([true, false])) pousser('Le nombre 0 a un inverse.', false);
    else pousser('0 n\'a pas d\'inverse.', true);

    const ordre = shuffle(lignes.map((_, i) => i));
    const L = ordre.map(i => lignes[i]);
    const B = ordre.map(i => bonnes[i]);
    const payload = makeVraiFaux(L, B,
        `<p>Ne confonds pas l'inverse de ${tex('x')}, qui est ${tex('\\dfrac{1}{x}')}, et l'opposé de ${tex('x')}, qui est ${tex('{-x}')}.</p>`
        + '<p><b>Vrai ou faux ?</b></p>',
        opts(L_VF));
    payload._meta.params = { lignes: L, bonnes: B };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    inverse_texte_trou_saisie: genInverse,
    reconnaitre_qcm_multiple: genReconnaitre,
    emboite_texte_trou_saisie: genEmboite,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_multiple: 'QCM multiple', tableau_vf: 'Vrai/Faux' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-03 (chantier groupes-differencies) ; sert aux exercices complexes du groupe Fort.
export const CATEGORIES = [
    { id: 'inverse', label: L_INVERSE, etapes: 1, formatsList: buildFormats('inverse') },
    { id: 'reconnaitre', label: L_RECONNAITRE, etapes: 2, formatsList: buildFormats('reconnaitre') },
    { id: 'emboite', label: L_EMBOITE, etapes: 3, formatsList: buildFormats('emboite') },
    { id: 'vf', label: L_VF, etapes: 1, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
